'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ContactForm } from './contact-form';
import { Heading } from '../ui/heading';
import { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Paragraph } from '../ui/paragraph';
import Link from '@eliadmin/core/src/components/link';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface CardProps {
  children?: React.ReactNode;
  icon: string;
  label: string;
  value: string;
  href: string;
  className?: string;
}

export function ContactContent({
  title,
  generalLayout,
  description,
}: {
  title: string;
  generalLayout: GeneralLayout['data'];
  description?: string;
}) {
  const { t } = useTranslations();
  return (
    <section className={cn('custom-section', 'pt-6 sm:pb-28 lg:pb-32', 'px-0 sm:px-5')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-start gap-0 sm:gap-6 md:flex-row md:items-stretch md:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-6 px-5 pb-16 sm:px-0 md:max-w-[652px] md:pb-0">
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <Heading title={title} tag="h2" />
            <Paragraph
              color="muted-foreground"
              innerHtml={description}
              className="w-full max-w-[540px]"
              size="md"
            />
          </div>
          <Card
            href={`tel:${generalLayout?.contact?.phone}`}
            icon="/images/icons/footer/call-calling-black.svg"
            label={t('text800')}
            value={generalLayout?.contact?.phone ?? ''}
          />
          <Card
            href={`mailto:${generalLayout?.contact?.email}`}
            icon="/images/icons/footer/sms-tracking-black.svg"
            label="Napíšte nám"
            value={generalLayout?.contact?.email ?? ''}
          />
          <Card
            href="https://www.google.com/maps/place/Teplick%C3%A1+3,+831+02+Bratislava/@48.1732013,17.1249772,209m/data=!3m1!1e3!4m6!3m5!1s0x476c8ea4b4df29e1:0x87857bad183c251d!8m2!3d48.173234!4d17.1255861!16s%2Fg%2F11x64tz781?entry=ttu&g_ep=EgoyMDI2MDExMy4wIKXMDSoASAFQAw%3D%3D"
            icon="/images/icons/location-black.svg"
            label={t('text3993')}
            value={`${generalLayout?.contact?.company}<br/>${generalLayout?.contact?.address}, ${generalLayout?.contact?.postcode} ${generalLayout?.contact?.city}`}
          >
            <Image
              src={'/images/mapka.png'}
              alt="Mapa"
              width={0}
              height={0}
              className="h-auto w-full rounded-[8px] object-cover object-center"
              sizes="100vw"
            />
          </Card>
        </div>
        <div className="w-full sm:max-w-[556px]">
          <ContactForm className="rounded-none px-5 py-16 sm:rounded-[8px] sm:px-8 sm:py-12 lg:px-12" />
        </div>
      </div>
    </section>
  );
}

function Card({ children, icon, label, value, href, className }: CardProps) {
  return (
    <Link
      href={href}
      prefetch={false}
      target="_blank"
      className={cn(
        'flex w-full flex-col items-start justify-start gap-4 rounded-[8px] border border-border p-4 transition-all hover:opacity-80 sm:gap-5 sm:p-5 lg:gap-6 lg:p-6',
        className,
      )}
    >
      <div className="flex w-full items-center justify-start gap-4 lg:gap-6">
        <div className="flex max-h-14 min-h-14 min-w-14 max-w-14 items-center justify-center rounded-[5px] bg-primary-foreground sm:max-h-16 sm:min-h-16 sm:min-w-16 sm:max-w-16 lg:max-h-[92px] lg:min-h-[92px] lg:min-w-[92px] lg:max-w-[92px]">
          <Image
            src={icon}
            alt={label}
            width={0}
            height={0}
            className="h-6 w-6 object-contain sm:h-8 sm:w-8"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-2">
          <Paragraph color="muted-foreground" size="lg">
            {label}
          </Paragraph>
          <Heading title={value} tag="h5" />
        </div>
      </div>
      {children}
    </Link>
  );
}
